import React, { useState, useEffect } from 'react';
import { Tag, Plus, Trash2, Check, X, AlertCircle, Loader2, DollarSign, Percent } from 'lucide-react';
import { discountService, DiscountConfig, DiscountCode } from '@/services/discount.service';

export default function DiscountManager() {
  const [discountConfig, setDiscountConfig] = useState<DiscountConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isSaleActive, setIsSaleActive] = useState(false);
  const [systemCode, setSystemCode] = useState('');
  const [showAddCode, setShowAddCode] = useState(false);

  // New code form state
  const [newCode, setNewCode] = useState('');
  const [newDiscountType, setNewDiscountType] = useState<'percentage' | 'flat'>('percentage');
  const [newCodePercentage, setNewCodePercentage] = useState(10);
  const [newCodeAmount, setNewCodeAmount] = useState(50);
  const [addingCode, setAddingCode] = useState(false);

  useEffect(() => { loadDiscountConfig(); }, []);
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const loadDiscountConfig = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await discountService.getDiscountConfig();
      if (response.success && response.data) {
        setDiscountConfig(response.data);
        setIsSaleActive(response.data.is_sale_active);
        setSystemCode(response.data.system_discount_code || '');
      } else {
        setError(response.error || 'Failed to load discount configuration');
      }
    } catch { setError('Failed to load discount configuration'); }
    finally { setLoading(false); }
  };

  const handleSaveConfig = async (overrideSaleActive?: boolean) => {
    setSaving(true);
    setError(null);
    try {
      const saleActiveValue = overrideSaleActive !== undefined ? overrideSaleActive : isSaleActive;
      const response = await discountService.updateDiscountConfig({
        is_sale_active: saleActiveValue,
        system_discount_code: systemCode.trim() || null
      });
      if (response.success && response.data) {
        setDiscountConfig(response.data);
        setIsSaleActive(response.data.is_sale_active);
        setSuccessMessage('Configuration saved successfully');
      } else { setError(response.error || 'Failed to save configuration'); }
    } catch { setError('Failed to save configuration'); }
    finally { setSaving(false); }
  };

  const handleToggleSaleActive = async () => {
    const newValue = !isSaleActive;
    setIsSaleActive(newValue);
    await handleSaveConfig(newValue);
  };

  const handleAddCustomCode = async () => {
    if (!newCode.trim()) {
      setError('Please enter a discount code');
      return;
    }

    // Validate based on discount type
    if (newDiscountType === 'percentage') {
      if (newCodePercentage < 1 || newCodePercentage > 100) {
        setError('Discount percentage must be between 1 and 100');
        return;
      }
    } else {
      if (newCodeAmount <= 0) {
        setError('Discount amount must be greater than 0');
        return;
      }
    }

    setAddingCode(true);
    setError(null);
    try {
      const response = await discountService.addCustomCode({
        code: newCode.trim().toUpperCase(),
        discount_type: newDiscountType,
        discount_percentage: newDiscountType === 'percentage' ? newCodePercentage : undefined,
        discount_amount: newDiscountType === 'flat' ? newCodeAmount : undefined,
        active: true
      });
      if (response.success && response.data) {
        setDiscountConfig(response.data);
        setNewCode('');
        setNewCodePercentage(10);
        setNewCodeAmount(50);
        setNewDiscountType('percentage');
        setShowAddCode(false);
        setSuccessMessage('Discount code added successfully');
      } else { setError(response.error || 'Failed to add discount code'); }
    } catch { setError('Failed to add discount code'); }
    finally { setAddingCode(false); }
  };

  const handleDeleteCode = async (code: string) => {
    if (!confirm(`Are you sure you want to delete the discount code "${code}"?`)) return;
    setError(null);
    try {
      const response = await discountService.deleteCustomCode(code);
      if (response.success && response.data) {
        setDiscountConfig(response.data);
        setSuccessMessage('Discount code deleted successfully');
      } else { setError(response.error || 'Failed to delete discount code'); }
    } catch { setError('Failed to delete discount code'); }
  };

  const formatDiscountDisplay = (code: DiscountCode) => {
    if (code.discount_type === 'flat' && code.discount_amount) {
      return `$${code.discount_amount} OFF`;
    }
    return `${code.discount_percentage || 0}% OFF`;
  };

  if (loading) {
    return (
      <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-12 text-center">
        <Loader2 className="w-12 h-12 text-purple-400 animate-spin mx-auto mb-4" />
        <p className="text-gray-400">Loading configuration...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {successMessage && (
        <div className="p-4 bg-green-500/20 border border-green-500/30 rounded-lg flex items-center gap-3">
          <Check className="w-5 h-5 text-green-400" />
          <span className="text-green-300">{successMessage}</span>
        </div>
      )}
      {error && (
        <div className="p-4 bg-red-500/20 border border-red-500/30 rounded-lg flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-400" />
          <span className="text-red-300">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto p-1 hover:bg-red-500/20 rounded"><X className="w-4 h-4 text-red-400" /></button>
        </div>
      )}

      <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-3 bg-purple-600/20 rounded-lg"><Tag className="w-6 h-6 text-purple-400" /></div>
          <div><h2 className="text-2xl font-bold">Discount Codes</h2><p className="text-gray-400 text-sm">Manage sale status and discount codes</p></div>
        </div>

        {/* Sale Active Toggle */}
        <div className="mb-8 p-6 bg-white/5 rounded-xl border border-white/10">
          <div className="flex items-center justify-between">
            <div><h3 className="text-lg font-semibold mb-1">Sale Active</h3><p className="text-gray-400 text-sm">When enabled, the system discount code will activate the products discount price</p></div>
            <button onClick={handleToggleSaleActive} disabled={saving} className={`relative w-14 h-8 rounded-full transition-colors ${saving ? 'opacity-50 cursor-not-allowed' : ''} ${isSaleActive ? 'bg-green-500' : 'bg-gray-600'}`}>
              <span className={`absolute top-1 left-1 w-6 h-6 bg-white rounded-full transition-transform ${isSaleActive ? 'translate-x-6' : 'translate-x-0'}`} />
            </button>
          </div>
        </div>

        {/* System Discount Code */}
        <div className="mb-8 p-6 bg-white/5 rounded-xl border border-white/10">
          <h3 className="text-lg font-semibold mb-3">System Discount Code</h3>
          <p className="text-gray-400 text-sm mb-4">When this code is used during checkout, customers get the products discount price (if sale is active)</p>
          <div className="flex gap-4">
            <input type="text" value={systemCode} onChange={(e) => setSystemCode(e.target.value.toUpperCase())} placeholder="e.g., HOLIDAY2025" className="flex-1 px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500" />
            <button onClick={() => handleSaveConfig()} disabled={saving} className="px-6 py-3 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg font-semibold transition-colors flex items-center gap-2">
              {saving ? (<><Loader2 className="w-4 h-4 animate-spin" />Saving...</>) : (<><Check className="w-4 h-4" />Save</>)}
            </button>
          </div>
        </div>

        {/* Custom Discount Codes */}
        <div className="p-6 bg-white/5 rounded-xl border border-white/10">
          <div className="flex items-center justify-between mb-4">
            <div><h3 className="text-lg font-semibold">Custom Discount Codes</h3><p className="text-gray-400 text-sm">Create percentage or flat rate discounts applied to full price</p></div>
            <button onClick={() => setShowAddCode(!showAddCode)} className="px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg font-semibold transition-colors flex items-center gap-2"><Plus className="w-4 h-4" />Add Code</button>
          </div>

          {showAddCode && (
            <div className="mb-6 p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Discount Code Input */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Discount Code</label>
                  <input
                    type="text"
                    value={newCode}
                    onChange={(e) => setNewCode(e.target.value.toUpperCase())}
                    placeholder="e.g., SUMMER25"
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
                  />
                </div>

                {/* Discount Type Selector */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Discount Type</label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setNewDiscountType('percentage')}
                      className={`flex-1 px-3 py-2 rounded-lg flex items-center justify-center gap-2 transition-colors ${
                        newDiscountType === 'percentage'
                          ? 'bg-purple-600 text-white'
                          : 'bg-white/5 text-gray-400 hover:bg-white/10'
                      }`}
                    >
                      <Percent className="w-4 h-4" />
                      <span>Percentage</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setNewDiscountType('flat')}
                      className={`flex-1 px-3 py-2 rounded-lg flex items-center justify-center gap-2 transition-colors ${
                        newDiscountType === 'flat'
                          ? 'bg-purple-600 text-white'
                          : 'bg-white/5 text-gray-400 hover:bg-white/10'
                      }`}
                    >
                      <DollarSign className="w-4 h-4" />
                      <span>Flat Rate</span>
                    </button>
                  </div>
                </div>

                {/* Discount Value Input */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    {newDiscountType === 'percentage' ? 'Discount Percentage' : 'Discount Amount ($)'}
                  </label>
                  {newDiscountType === 'percentage' ? (
                    <div className="relative">
                      <input
                        type="number"
                        min="1"
                        max="100"
                        value={newCodePercentage}
                        onChange={(e) => setNewCodePercentage(parseInt(e.target.value) || 0)}
                        className="w-full px-4 py-2 pr-8 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">%</span>
                    </div>
                  ) : (
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                      <input
                        type="number"
                        min="1"
                        step="0.01"
                        value={newCodeAmount}
                        onChange={(e) => setNewCodeAmount(parseFloat(e.target.value) || 0)}
                        className="w-full px-4 py-2 pl-8 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
                      />
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex items-end gap-2">
                  <button
                    onClick={handleAddCustomCode}
                    disabled={addingCode}
                    className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg font-semibold transition-colors flex items-center justify-center gap-2"
                  >
                    {addingCode ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                    Add
                  </button>
                  <button
                    onClick={() => {
                      setShowAddCode(false);
                      setNewCode('');
                      setNewCodePercentage(10);
                      setNewCodeAmount(50);
                      setNewDiscountType('percentage');
                    }}
                    className="px-4 py-2 bg-gray-600 hover:bg-gray-700 rounded-lg font-semibold transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          )}

          {discountConfig?.custom_codes && discountConfig.custom_codes.length > 0 ? (
            <div className="space-y-3">
              {discountConfig.custom_codes.map((code: DiscountCode) => (
                <div key={code.code} className="flex items-center justify-between p-4 bg-white/5 rounded-lg border border-white/10">
                  <div className="flex items-center gap-4">
                    <div className="px-3 py-1 bg-purple-600/20 border border-purple-500/30 rounded font-mono text-purple-300">{code.code}</div>
                    <div className="flex items-center gap-2">
                      {code.discount_type === 'flat' ? (
                        <DollarSign className="w-4 h-4 text-green-400" />
                      ) : (
                        <Percent className="w-4 h-4 text-green-400" />
                      )}
                      <span className="text-lg font-semibold text-green-400">{formatDiscountDisplay(code)}</span>
                    </div>
                    <span className={`px-2 py-1 text-xs rounded ${
                      code.discount_type === 'flat'
                        ? 'bg-blue-500/20 text-blue-400'
                        : 'bg-purple-500/20 text-purple-400'
                    }`}>
                      {code.discount_type === 'flat' ? 'Flat Rate' : 'Percentage'}
                    </span>
                    {code.active ? (
                      <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded">Active</span>
                    ) : (
                      <span className="px-2 py-1 bg-gray-500/20 text-gray-400 text-xs rounded">Inactive</span>
                    )}
                  </div>
                  <button
                    onClick={() => handleDeleteCode(code.code)}
                    className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors"
                    title="Delete code"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">No custom discount codes yet. Click Add Code to create one.</p>
          )}
        </div>
      </div>
    </div>
  );
}
